import { createApi } from "@reduxjs/toolkit/query/react";
import * as Highcharts from "highcharts";

import { FIELD_CLIMATE_API_ENDPOINT } from "../../../config/api";
import { baseQuery } from "../../../shared/api/base-query";
import { createQueryFn } from "../../../shared/api/create-query-fn";
import { pagedQueryFn } from "../../../shared/api/paged-query-fn";
import { PagedResponseDto } from "../../../shared/dtos/paged-response.dto";
import { Order } from "../../../shared/enums/order";
import { buildFilterCondition } from "../../../shared/utils/build-filter-condition";
import { buildFilterExpression } from "../../../shared/utils/build-filter-expression";
import { toStartOfDayUTCTimestamp } from "../../../utils/utils";
import { ICropRotationDto } from "../shared/dtos/crop-rotation.dto";
import { IFarmLandDto } from "../shared/dtos/farm-land.dto";
import { ChartPeriod } from "../shared/enums/chart-period";

const DECIMAL_INTEGER_PATTERN = /^-?\d+$/;

export interface IWeatherChartsFilter {
  stationId: string;
  from: Date;
  to: Date;
  period: ChartPeriod;
}

export const farmLandsApi = createApi({
  reducerPath: "farmLandsApi",
  baseQuery,
  endpoints: (builder) => ({
    fetchFarmLandById: builder.query<IFarmLandDto, string>({
      query: (id) => ({ url: `FarmLandsV2/${id}`, method: "GET" }),
    }),

    fetchFarmLandByArcGisId: builder.query<IFarmLandDto | null, string>({
      queryFn: createQueryFn(async (id, executeBaseQuery) => {
        if (!DECIMAL_INTEGER_PATTERN.test(id)) {
          return { data: null };
        }
        const arcgisObjectId = Number(id);
        if (!Number.isSafeInteger(arcgisObjectId)) {
          return { data: null };
        }
        const response = await executeBaseQuery({
          url: "FarmLandsV2",
          method: "GET",
          params: {
            pageNumber: 1,
            pageSize: 1,
            filter: buildFilterExpression(
              buildFilterCondition("isDeleted", "!=", true),
              buildFilterCondition("arcgisObjectId", "==", arcgisObjectId)
            ),
          },
        });
        if (response.error !== undefined) {
          return { error: response.error };
        }
        if (!isFarmLandPagedResponse(response.data)) {
          return {
            error: {
              status: "CUSTOM_ERROR",
              error: "Invalid FarmLandsV2 response",
            },
          };
        }
        return {
          data: response.data.data[0] ?? null,
        };
      }),
    }),

    fetchCropRotationList: builder.query<ICropRotationDto[], string>({
      queryFn: pagedQueryFn<ICropRotationDto, ICropRotationDto[], string>(
        (id) => ({
          url: "CropRotationsV2",
          method: "GET",
          params: {
            filter: buildFilterExpression(
              buildFilterCondition("isDeleted", "!=", true),
              buildFilterCondition("farmLandId", "==", id)
            ),
            sort: `year ${Order.DESC}`,
          },
        }),
        (dtos) => dtos,
        1000
      ),
    }),

    fetchWeatherCharts: builder.query<Highcharts.Options[], IWeatherChartsFilter>({
      query: ({ stationId, from, to, period }) => {
        const startFrom = toStartOfDayUTCTimestamp(Math.floor(from.getTime() / 1000));
        const startTo = toStartOfDayUTCTimestamp(Math.floor(to.getTime() / 1000));
        const fieldClimateUrl = `${FIELD_CLIMATE_API_ENDPOINT}/chart/highchart/${stationId}/${period}/from/${startFrom}/to/${startTo}`;
        return {
          url: `FieldClimate/request?url=${encodeURIComponent(fieldClimateUrl)}`,
          method: "GET",
          headers: { "accept-language": "ru" },
        };
      },
      transformResponse: (chartDtos: Highcharts.Options[]) =>
        Array.isArray(chartDtos) ? chartDtos : [],
    }),
  }),
});

function isFarmLandPagedResponse(
  response: unknown
): response is PagedResponseDto<IFarmLandDto> {
  return (
    response !== null &&
    typeof response === "object" &&
    "data" in response &&
    Array.isArray(response.data)
  );
}

export const {
  useFetchFarmLandByIdQuery,
  useFetchFarmLandByArcGisIdQuery,
  useFetchCropRotationListQuery,
  useFetchWeatherChartsQuery,
} = farmLandsApi;
export const farmLandsApiReducer = farmLandsApi.reducer;
